// Top-level page composition — TopBar, ConfigScreen, Dashboard,
// running overlay, details modal, deploy toasts. Ported from
// design/design_handoff_nifty_bull/prototype/variant-a.jsx and wired
// to the real backend through window.API.

const POLL_INTERVAL_MS = 800;
const DEFAULT_AMOUNT = 500000;
const DEFAULT_MONTHS = 12;
// Default cap on per-trade size as a fraction of total capital. 5% is
// the value the dashboard used to hardcode — surface it as a control
// instead so the user can dial concentration up or down. The ₹10k
// floor below stays so a 1% setting on a tiny capital pool still
// produces a workable trade size.
const DEFAULT_MAX_PCT = 5;
const MIN_TRADE_FLOOR = 10000;

// Default backtest window: most recent 1 year through "today" (capped to
// the design's reference end-date so a fresh deploy with no market data
// for the past week still has a non-empty window).
function defaultDates() {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const startD = new Date(today.getTime() - 365 * 864e5);
    return { start: startD.toISOString().slice(0, 10), end };
}

/** Clamp an ISO date string to >= floor and <= ceil (both inclusive). */
function clampDate(iso, floor, ceil) {
    if (floor && iso < floor) return floor;
    if (ceil && iso > ceil) return ceil;
    return iso;
}

function App() {
    const [strategies, setStrategies] = React.useState([]);
    const [stratLoadError, setStratLoadError] = React.useState(null);
    const [strategyId, setStrategyId] = React.useState(null);
    // Map of technical-term → educational URL. Populated once on mount;
    // {} until then so StrategyModal renders cleanly with no links.
    const [glossary, setGlossary] = React.useState({});
    const [universes, setUniverses] = React.useState([]);
    const [universeId, setUniverseId] = React.useState('NIFTY_50');
    const [mode, setMode] = React.useState('lumpsum');
    const [amount, setAmount] = React.useState(DEFAULT_AMOUNT);
    const [months, setMonths] = React.useState(DEFAULT_MONTHS);
    const [maxPct, setMaxPct] = React.useState(DEFAULT_MAX_PCT);
    const dd = React.useMemo(defaultDates, []);
    const [startDate, setStartDate] = React.useState(dd.start);
    const [endDate, setEndDate] = React.useState(dd.end);
    const [detailsId, setDetailsId] = React.useState(null);
    // Server-sourced engine config: `historyStartDate` is the
    // earliest date the backend will accept (any earlier and the
    // backtest hangs trying to fetch non-existent data — see
    // bug fix in DashboardResource.validateDateBounds). `today` is
    // the server's idea of the calendar end. Both used as min/max
    // on the date inputs.
    const [engineConfig, setEngineConfig] = React.useState(null);

    const [screen, setScreen] = React.useState('config'); // config | running | dashboard
    // ── Atelier Bull v2: command palette + onboarding curtain. ──
    // Curtain shows once per browser (localStorage gated); palette
    // toggles on Cmd-K / Ctrl-K and exposes strategy switching +
    // run/reset commands.
    const [paletteOpen, setPaletteOpen] = React.useState(false);
    const [showCurtain, setShowCurtain] = React.useState(
        () => typeof window !== 'undefined' && FX.shouldShowOnboarding()
    );
    React.useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    // Route screen transitions through the View Transitions API when
    // available so the swap between config → running → dashboard
    // cross-fades instead of hard-cutting. Falls back to a plain
    // setScreen on browsers without `document.startViewTransition`
    // (Safari < 18.2, Firefox without flag) — those users still get
    // the existing inner-card slide-up animations. Per
    // vercel-react-view-transitions skill: the browser handles the
    // tween itself; we just opt in.
    const transitionToScreen = React.useCallback((next) => {
        if (typeof document !== 'undefined' &&
            typeof document.startViewTransition === 'function') {
            document.startViewTransition(() => {
                React.startTransition(() => setScreen(next));
            });
        } else {
            setScreen(next);
        }
    }, []);
    const [jobId, setJobId] = React.useState(null);
    const [jobStatus, setJobStatus] = React.useState(null);
    const [result, setResult] = React.useState(null);
    const [runError, setRunError] = React.useState(null);

    const [toast, setToast] = React.useState(null);

    // Fetch strategies + universes + engine config on mount.
    React.useEffect(() => {
        API.listStrategies()
            .then((list) => {
                setStrategies(list);
                if (list.length && !strategyId) setStrategyId(list[0].id);
            })
            .catch((e) => setStratLoadError(e.message || 'Failed to load strategies'));
        API.listUniverses()
            .then(setUniverses)
            .catch((e) => console.warn('Failed to load universes:', e.message));
        API.getGlossary()
            .then(setGlossary)
            .catch((e) => console.warn('Failed to load glossary (term auto-links disabled):', e.message));
        API.getConfig()
            .then((cfg) => {
                setEngineConfig(cfg);
                // Clamp existing date state to the floor/ceiling the
                // server just told us about. If the user happens to
                // have stale localStorage values (today: not yet in
                // play, but defensive) or a clock-skewed default,
                // this prevents an immediate 400 on Run.
                setStartDate((d) => clampDate(d, cfg.historyStartDate, cfg.today));
                setEndDate((d) => clampDate(d, cfg.historyStartDate, cfg.today));
            })
            .catch((e) => console.warn('Failed to load engine config:', e.message));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll job status while running.
    React.useEffect(() => {
        if (screen !== 'running' || !jobId) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const data = await API.getBacktestStatus(jobId);
                if (cancelled) return;
                setJobStatus(data.status);
                if (data.status === 'COMPLETED') {
                    setResult(data.result);
                    transitionToScreen('dashboard');
                    // Celebration moment — confetti + soft chime when
                    // the backtest finishes profitable. Loss plays a
                    // gentler descending tone instead. Both gated by
                    // the user's reduce-motion + Web Audio gesture
                    // policy (FX module short-circuits if blocked).
                    const ret = data.result?.kpis?.totalReturnPct ?? 0;
                    if (ret > 0) {
                        FX.runConfetti({ count: 90 });
                        FX.playChime('profit');
                    } else if (ret < 0) {
                        FX.playChime('loss');
                    } else {
                        FX.playChime('complete');
                    }
                } else if (data.status === 'FAILED') {
                    setRunError(data.error || 'Backtest failed');
                    transitionToScreen('config');
                }
            } catch (e) {
                if (cancelled) return;
                setRunError(e.message || 'Polling failed');
                transitionToScreen('config');
            }
        };
        const id = setInterval(tick, POLL_INTERVAL_MS);
        tick();
        return () => { cancelled = true; clearInterval(id); };
    }, [screen, jobId]);

    const selected = strategies.find((s) => s.id === strategyId);

    const onRun = async () => {
        if (!selected) return;
        setRunError(null);
        setResult(null);
        setJobStatus('QUEUED');
        transitionToScreen('running');
        try {
            const body = {
                strategyName: strategyId,
                universe: universeId,
                startDate, endDate,
                initialCash: amount,
                minTradeAmount: 1000,
                // ₹10k floor preserves the previous safety net: a 1%
                // setting on a small capital pool still produces a
                // workable trade size rather than a sub-1k order.
                maxTradeAmount: Math.max(MIN_TRADE_FLOOR, Math.floor(amount * (maxPct / 100))),
                mode: mode === 'sip' ? 'SIP' : 'LUMPSUM',
                months: mode === 'sip' ? months : null,
            };
            const { jobId: newJobId } = await API.submitBacktest(body);
            setJobId(newJobId);
        } catch (e) {
            setRunError(e.message || 'Failed to submit backtest');
            transitionToScreen('config');
        }
    };

    // Deploy-to-broker is aspirational and not yet implemented.
    // The Dashboard header used to show a "Deploy live" button here;
    // when that capability returns it will use a fresh wire to a real
    // adapter rather than the previous half-implemented path.

    const reset = () => {
        transitionToScreen('config');
        setJobId(null);
        setJobStatus(null);
        setResult(null);
    };

    if (stratLoadError) {
        return (
            <div className="app-shell">
                <TopBar screen="config" />
                <div style={{ maxWidth: 720, margin: '80px auto', padding: 24 }}>
                    <div className="card" style={{ padding: 24 }}>
                        <h2 style={{ marginTop: 0 }}>Couldn't load strategies</h2>
                        <p style={{ color: 'var(--text-2)' }}>{stratLoadError}</p>
                        <button className="btn" onClick={() => window.location.reload()}>Reload</button>
                    </div>
                </div>
            </div>
        );
    }

    if (strategies.length === 0) {
        return (
            <div className="app-shell">
                <TopBar screen="config" />
                <div className="fade-in" style={{ maxWidth: 1120, margin: '0 auto', padding: '48px 40px 72px' }} aria-busy="true">
                    {/* Skeleton silhouette of the Config screen — same
                        rough shape as the real content, so first paint
                        doesn't look 'broken' while strategies load. */}
                    <div className="mb-6">
                        <div className="skeleton line mb-3" style={{ width: 110, height: 11 }} />
                        <div className="skeleton mb-3" style={{ width: 380, height: 44 }} />
                        <div className="skeleton line-lg" style={{ width: '100%', maxWidth: 620 }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 'var(--space-6)', alignItems: 'start' }}>
                        <div className="col gap-5">
                            <div className="card p-7"><div className="skeleton block" /></div>
                            <div className="card p-7"><div className="skeleton block" /></div>
                            <div className="card p-7"><div className="skeleton block" style={{ height: 120 }} /></div>
                        </div>
                        <div className="card p-5"><div className="skeleton block" style={{ height: 240 }} /></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="app-shell">
            <TopBar screen={screen === 'dashboard' || screen === 'running' ? 'dashboard' : 'config'} />

            {screen === 'config' && (
                <ConfigScreen
                    strategies={strategies}
                    strategyId={strategyId} setStrategyId={setStrategyId}
                    universes={universes}
                    universeId={universeId} setUniverseId={setUniverseId}
                    mode={mode} setMode={setMode}
                    amount={amount} setAmount={setAmount}
                    months={months} setMonths={setMonths}
                    maxPct={maxPct} setMaxPct={setMaxPct}
                    startDate={startDate} setStartDate={setStartDate}
                    endDate={endDate} setEndDate={setEndDate}
                    engineConfig={engineConfig}
                    onSeeDetails={setDetailsId}
                    onRun={onRun}
                    runError={runError}
                />
            )}

            {(screen === 'running' || screen === 'dashboard') && result && (
                <Dashboard
                    strategy={selected}
                    mode={mode} amount={amount} months={months}
                    startDate={startDate} endDate={endDate}
                    summary={result}
                    onReset={reset}
                />
            )}

            {screen === 'running' && (
                <UI.BacktestRunning strategy={selected} jobStatus={jobStatus} />
            )}

            {detailsId && (
                <UI.StrategyModal strategy={strategies.find((s) => s.id === detailsId)} glossary={glossary} onClose={() => setDetailsId(null)} />
            )}

            {toast && (
                <div className={`toast ${toast.kind}`}>{toast.text}</div>
            )}

            <FX.CommandPalette
                open={paletteOpen}
                onClose={() => setPaletteOpen(false)}
                commands={buildCommands({
                    strategies, setStrategyId,
                    onRun, reset,
                    screen,
                    setMode, mode,
                })}
            />

            {showCurtain && (
                <FX.OnboardingCurtain onDismiss={() => setShowCurtain(false)} />
            )}
        </div>
    );
}

// ── Command palette command set. Built fresh on each render so the
// captured closures (setStrategyId, onRun, reset) always point at
// the current handlers. The palette host filters / dispatches —
// this just enumerates what's runnable from the keyboard.
function buildCommands({ strategies, setStrategyId, onRun, reset, screen, setMode, mode }) {
    const cmds = [];
    if (screen !== 'config') {
        cmds.push({ id: 'nav-config', section: 'Navigate', label: 'New backtest',
                    hint: 'go back', run: reset });
    }
    if (screen === 'config') {
        cmds.push({ id: 'run', section: 'Action', label: 'Run backtest',
                    hint: '↵', run: onRun });
        cmds.push({ id: 'mode-lump', section: 'Mode',
                    label: mode === 'lumpsum' ? 'Mode: Lumpsum (active)' : 'Switch to Lumpsum',
                    run: () => setMode('lumpsum') });
        cmds.push({ id: 'mode-sip', section: 'Mode',
                    label: mode === 'sip' ? 'Mode: Monthly SIP (active)' : 'Switch to Monthly SIP',
                    run: () => setMode('sip') });
    }
    for (const s of strategies) {
        cmds.push({
            id: `strat-${s.id}`,
            section: 'Strategy',
            label: s.name,
            hint: s.category,
            run: () => setStrategyId(s.id),
        });
    }
    return cmds;
}

function TopBar({ screen }) {
    return (
        <div className="topbar row between" style={{
            padding: '18px 40px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface)',
        }}>
            <div className="row gap-3">
                <div className="row center" style={{
                    width: 44, height: 44, borderRadius: 'var(--radius)',
                    background: 'var(--accent-softer)',
                    color: 'var(--accent-text)',
                    border: '1px solid var(--accent-soft)',
                    boxShadow: '0 4px 12px -4px var(--accent-ring), inset 0 1px 0 0 oklch(1 0 0 / 0.4)',
                }}>
                    <FX.BullMascot size={32} />
                </div>
                <div className="col" style={{ lineHeight: 1 }}>
                    <div translate="no" style={{
                        fontFamily: 'var(--font-display)',
                        fontVariationSettings: "'opsz' 144",
                        fontSize: '22px', fontWeight: 700, letterSpacing: 'var(--tracking-display)',
                    }}>Nifty Bull</div>
                    <div className="mono tx-3 mt-1" translate="no" style={{
                        fontSize: 'var(--text-xs)', letterSpacing: 'var(--tracking-eyebrow)',
                        textTransform: 'uppercase',
                    }}>Algo Trading</div>
                </div>
                <span className="topbar-pill pill t-xs" style={{ marginLeft: 'var(--space-2)' }}>
                    {screen === 'config' ? 'New backtest' : 'Results'}
                </span>
            </div>
            <div className="row gap-5">
                <div className="topbar-status row gap-2 t-sm tx-2">
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 0 3px var(--accent-ring)' }} />
                    <span translate="no">NSE</span> · Historical
                </div>
                <div className="row center fw-600 t-sm tx-accent" style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'var(--accent-soft)',
                }}>AP</div>
            </div>
        </div>
    );
}

function ConfigScreen({ strategies, strategyId, setStrategyId, universes, universeId, setUniverseId,
    mode, setMode, amount, setAmount, months, setMonths, maxPct, setMaxPct,
    startDate, setStartDate, endDate, setEndDate,
    engineConfig,
    onSeeDetails, onRun, runError }) {
    // History floor + today as known by the server. While engineConfig
    // is still loading, we use sane fallbacks (2006-01-01 matches the
    // default in TradingAppConfiguration; today is the local clock).
    const historyMin = engineConfig?.historyStartDate || '2006-01-01';
    const todayMax   = engineConfig?.today || new Date().toISOString().slice(0, 10);
    const selected = strategies.find((s) => s.id === strategyId) || strategies[0];
    const selectedUniverse = (universes || []).find((u) => u.id === universeId);

    const capitalPresets = [
        { v: 100000, label: '1L' },
        { v: 250000, label: '2.5L' },
        { v: 500000, label: '5L' },
        { v: 1000000, label: '10L' },
        { v: 2500000, label: '25L' },
    ];

    // Quick-range chips end at server's `today`; start clamped to the
    // history floor so a 5-year chip on a fresh deploy doesn't try to
    // pick 2021-04 when the floor is 2024-01 (or 1990 with our 2006
    // floor).
    const dateRanges = [
        { label: '6 months', s: shiftDays(todayMax, -180), e: todayMax },
        { label: '1 year',   s: shiftDays(todayMax, -365), e: todayMax },
        { label: '2 years',  s: shiftDays(todayMax, -730), e: todayMax },
        { label: '5 years',  s: shiftDays(todayMax, -1825), e: todayMax },
    ].map((r) => ({ ...r, s: clampDate(r.s, historyMin, todayMax) }));

    const isRangeMatch = (s, e) => startDate === s && endDate === e;

    return (
        <div className="fade-in page-pad-config" style={{ maxWidth: 1120, margin: '0 auto', padding: '48px 40px 72px' }}>
            <div className="mb-6">
                <div className="eyebrow mb-3">New backtest</div>
                <h1 className="t-disp fw-600 trk-display" style={{ margin: 0, lineHeight: 1.05 }}>
                    Let's configure your bot.
                </h1>
                <p className="tx-2 mt-3" style={{ margin: 0, fontSize: 'var(--text-lg)', maxWidth: 620, lineHeight: 1.55 }}>
                    Pick a strategy, set how you'll fund it, and choose a window. We'll run the simulation on historical NSE data and show you exactly how it would have performed.
                </p>
                {runError && (
                    <div className="card mt-5" style={{
                        position: 'static', maxWidth: 620,
                        padding: 'var(--space-5)',
                        borderColor: 'var(--down)',
                        display: 'flex',
                        gap: 'var(--space-4)',
                        alignItems: 'center',
                    }}>
                        <img src="./assets/scenes/failed.png" alt=""
                             width="120" height="120"
                             loading="lazy" decoding="async" draggable={false}
                             style={{ width: 120, height: 120, objectFit: 'contain', flex: 'none' }} />
                        <div>
                            <div className="eyebrow tx-down" style={{ marginBottom: 'var(--space-1)' }}>
                                Backtest didn't complete
                            </div>
                            <div className="fw-600 tx" style={{ fontSize: 'var(--text-md)', marginBottom: 'var(--space-2)' }}>
                                {runError}
                            </div>
                            <div className="t-sm tx-3">
                                Tweak the inputs and try again. Common causes: empty universe, weekend-only window, or backend hiccup.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid-config">
                <div className="col gap-5">
                    <div className="card p-7">
                        <SectionHead step="01" title="Strategy" subtitle={`Choose one of ${strategies.length} pre-built algorithmic strategies`} />
                        <UI.StrategyPicker strategies={strategies} value={strategyId} onChange={setStrategyId} />
                        <div className="row between gap-3 mt-4 p-4" style={{
                            background: 'var(--bg-2)', borderRadius: 'var(--radius)',
                            border: '1px solid var(--border)',
                        }}>
                            <p className="t-base tx-2" style={{ margin: 0, lineHeight: 1.55 }}>
                                {selected.short}
                            </p>
                            <button className="btn sm flex-none" onClick={() => onSeeDetails(selected.id)}>
                                <UI.Icon.Info /> See details
                            </button>
                        </div>
                    </div>

                    <div className="card p-7">
                        <SectionHead step="02" title="Universe"
                            subtitle="Which slice of the NSE the strategy can pick stocks from" />
                        <div className="seg mb-3">
                            {(universes || []).map((u) => (
                                <button key={u.id}
                                    className={u.id === universeId ? 'active' : ''}
                                    onClick={() => setUniverseId(u.id)}>
                                    {u.name}
                                </button>
                            ))}
                            {(!universes || universes.length === 0) && (
                                <div className="t-sm tx-3" style={{ padding: '8px 12px' }}>
                                    Loading universes…
                                </div>
                            )}
                        </div>
                        {selectedUniverse && (
                            <div className="t-base tx-2 p-4" style={{
                                background: 'var(--bg-2)', borderRadius: 'var(--radius)',
                                lineHeight: 1.55,
                                border: '1px solid var(--border)',
                            }}>
                                {selectedUniverse.description}
                                <span className="t-sm tx-3 mt-2" style={{ display: 'block' }}>
                                    {selectedUniverse.constituentCount > 0
                                        ? `${selectedUniverse.constituentCount} constituents currently loaded.`
                                        : 'Universe not loaded yet — click Run to seed it from NSE.'}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="card p-7">
                        <SectionHead step="03" title="Capital allocation" subtitle="All at once, or drip-feed it monthly" />
                        <div className="seg mb-5">
                            <button className={mode === 'lumpsum' ? 'active' : ''} onClick={() => setMode('lumpsum')}>Lumpsum</button>
                            <button className={mode === 'sip' ? 'active' : ''} onClick={() => setMode('sip')}>Monthly SIP</button>
                        </div>
                        <div className="grid-capital" style={{ gridTemplateColumns: mode === 'sip' ? '1fr 1fr' : '1fr' }}>
                            <div>
                                <label className="label">Total capital (₹)</label>
                                <div style={{ position: 'relative' }}>
                                    <span className="tx-3" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--text-lg)' }}>₹</span>
                                    <input className="input mono fw-600"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        style={{ paddingLeft: 30, fontSize: 'var(--text-lg)', padding: '14px 14px 14px 30px' }}
                                        value={amount.toLocaleString('en-IN')}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value.replace(/[^\d]/g, ''), 10) || 0;
                                            setAmount(v);
                                        }} />
                                </div>
                                <div className="row wrap gap-2 mt-3">
                                    {capitalPresets.map((p) => (
                                        <button key={p.v} type="button"
                                            className={`btn xs chip ${amount === p.v ? 'selected' : ''}`}
                                            onClick={() => setAmount(p.v)}>
                                            ₹{p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {mode === 'sip' && (
                                <div>
                                    <label className="label">Split across (months)</label>
                                    <input className="input mono fw-600"
                                        type="number"
                                        inputMode="numeric"
                                        min="2" max="60" value={months}
                                        style={{ fontSize: 'var(--text-lg)', padding: '14px 14px' }}
                                        onChange={(e) => setMonths(Math.max(2, Math.min(60, parseInt(e.target.value, 10) || 12)))} />
                                    <div className="t-sm tx-accent mt-3" style={{ padding: '10px 12px', background: 'var(--accent-softer)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent-soft)' }}>
                                        <span className="mono fw-600">
                                            {UI.fmtINR(amount / months, { compact: true })}
                                        </span> deployed on the 1st of each month
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Risk-per-trade — caps how much capital any single buy can
                            deploy. Enforced inside each Strategy impl
                            (AAMR/RSISniper/Pullback all clamp to maxTradeAmount).
                            Lower → many small bets, more diversified, takes longer
                            to deploy; higher → fewer concentrated bets, larger
                            drawdowns. */}
                        <div className="mt-6" style={{ paddingTop: 'var(--space-5)', borderTop: '1px solid var(--border)' }}>
                            <div className="row-base between mb-3">
                                <label className="label row gap-2" style={{ marginBottom: 0 }}>
                                    Max per trade
                                    <UI.Hint width={300}>
                                        <strong>Max per Trade</strong> — hard cap on how much capital
                                        any single BUY can deploy, expressed as a percent of total
                                        capital. Each strategy clamps its position-sizing logic to this
                                        ceiling, so a strong signal can't put more than this fraction
                                        on one stock. Lower → more trades, more diversification, slower
                                        to deploy. Higher → fewer concentrated bets, larger drawdowns
                                        if any one position turns. The resolved ₹ figure has a{' '}
                                        <code>₹10,000</code> floor so a 1% setting on small capital
                                        still produces a workable trade size.
                                    </UI.Hint>
                                </label>
                                <div className="mono tnum t-base fw-600 tx">
                                    {maxPct}% ·{' '}
                                    <span className="tx-2">
                                        {UI.fmtINR(Math.max(MIN_TRADE_FLOOR, Math.floor(amount * (maxPct / 100))), { compact: true })}
                                    </span>
                                </div>
                            </div>
                            <input type="range" min="1" max="25" step="1"
                                value={maxPct}
                                onChange={(e) => setMaxPct(parseInt(e.target.value, 10) || 1)}
                                style={{ width: '100%', accentColor: 'var(--accent)' }} />
                            <div className="row between mt-1 t-xs tx-3 mono">
                                <span>1% · diversified</span>
                                <span>5% · default</span>
                                <span>25% · concentrated</span>
                            </div>
                        </div>
                    </div>

                    <div className="card p-7">
                        <SectionHead step="04" title="Backtest window" subtitle="Which historical period to simulate" />
                        <div className="grid-dates mb-3">
                            <div>
                                <label className="label">Start date</label>
                                <input className="input" type="date"
                                    min={historyMin} max={todayMax}
                                    value={startDate}
                                    onChange={(e) => setStartDate(clampDate(e.target.value, historyMin, todayMax))} />
                            </div>
                            <div>
                                <label className="label">End date</label>
                                <input className="input" type="date"
                                    min={startDate || historyMin} max={todayMax}
                                    value={endDate}
                                    onChange={(e) => setEndDate(clampDate(e.target.value, historyMin, todayMax))} />
                            </div>
                        </div>
                        <div className="row wrap gap-2">
                            {dateRanges.map((p) => (
                                <button key={p.label} type="button"
                                    className={`btn xs chip ${isRangeMatch(p.s, p.e) ? 'selected' : ''}`}
                                    onClick={() => { setStartDate(p.s); setEndDate(p.e); }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="summary-rail" style={{ position: 'sticky', top: 'var(--space-6)' }}>
                    <div className="card p-5" style={{ boxShadow: 'var(--shadow-md)' }}>
                        <div className="eyebrow mb-4">Summary</div>
                        <SummaryRow label="Strategy" value={selected.name} />
                        <SummaryRow label="Category" value={selected.category} />
                        <SummaryRow label="Capital" value={UI.fmtINR(amount, { compact: true })} />
                        <SummaryRow label="Mode" value={mode === 'lumpsum' ? 'Lumpsum' : `SIP × ${months}mo`} />
                        <SummaryRow label="Max / trade"
                            value={`${maxPct}% · ${UI.fmtINR(Math.max(MIN_TRADE_FLOOR, Math.floor(amount * (maxPct / 100))), { compact: true })}`} />
                        <SummaryRow label="From" value={UI.fmtDate(new Date(startDate))} />
                        <SummaryRow label="To" value={UI.fmtDate(new Date(endDate))} />
                        <SummaryRow label="Universe"
                            value={selectedUniverse ? selectedUniverse.name : universeId.replace('_', ' ')}
                            last />

                        <button className="btn accent center mt-5" style={{ width: '100%', padding: 'var(--space-3)', fontSize: 'var(--text-md)' }} onClick={onRun}>
                            <UI.Icon.Play /> Run backtest
                        </button>
                        <div className="t-xs tx-3 mt-3" style={{ textAlign: 'center' }}>
                            Simulation runs on the server — usually 5–30s depending on window.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function shiftDays(isoDate, delta) {
    const d = new Date(isoDate);
    d.setDate(d.getDate() + delta);
    return d.toISOString().slice(0, 10);
}

function SectionHead({ step, title, subtitle }) {
    return (
        <div className="row gap-3 mb-5">
            <div className="mono row center fw-600 tx-2 t-sm" style={{
                width: 32, height: 32, borderRadius: 'var(--radius-sm)',
                background: 'var(--surface-2)', border: '1px solid var(--border)',
            }}>{step}</div>
            <div>
                <div className="fw-600" style={{ fontSize: 'var(--text-lg)', letterSpacing: 'var(--tracking-heading)' }}>{title}</div>
                <div className="t-sm tx-3 mt-1">{subtitle}</div>
            </div>
        </div>
    );
}

function SummaryRow({ label, value, last }) {
    return (
        <div className="row between gap-3" style={{
            padding: '10px 0', borderBottom: last ? 'none' : '1px solid var(--border)',
        }}>
            <span className="t-sm tx-3">{label}</span>
            <span className="mono tnum t-sm fw-600 tx" style={{ textAlign: 'right' }}>{value}</span>
        </div>
    );
}

function Dashboard({ strategy, mode, amount, months, startDate, endDate, summary, onReset }) {
    const kpis = summary.kpis || {};
    const curve = summary.curve || [];
    const trades = summary.trades || [];

    return (
        <div className="stagger page-pad-dashboard" style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 40px 72px' }}>
            <div className="fade-in row-start between gap-4 mb-6">
                <div>
                    <button className="btn ghost sm mb-3" onClick={onReset} style={{ padding: '6px 10px', marginLeft: -10 }}>
                        <UI.Icon.Back /> New backtest
                    </button>
                    <div className="row gap-2 mb-3">
                        <span className="pill accent">{strategy.category}</span>
                        <span className="pill">{mode === 'lumpsum' ? 'Lumpsum' : `SIP × ${months}mo`}</span>
                        <span className="pill">{UI.fmtINR(amount, { compact: true })}</span>
                    </div>
                    <h1 className="t-3xl fw-600 trk-display" style={{ margin: 0, lineHeight: 1.1 }}>
                        {strategy.name} · Backtest results
                    </h1>
                    <div className="t-base tx-3 mt-2">
                        {UI.fmtDate(new Date(startDate))} — {UI.fmtDate(new Date(endDate))} · {summary.benchmarkLabel || 'NIFTY 50'} universe
                    </div>
                </div>
                {/* Streak flag — pops in when ≥5 trades on the run
                    closed profitable. Quietly absent otherwise. */}
                {(() => {
                    const winners = trades.filter((t) => (t.pnl || 0) > 0).length;
                    if (winners < 5) return null;
                    return (
                        <span className="streak-flag" title={`${winners} profitable trades`}>
                            <span className="streak-spark" />
                            {winners} winning trades
                        </span>
                    );
                })()}

                {/* Outcome bull — the PNG mascot reflects how the
                    strategy did. Profit shows the celebrating pose,
                    loss shows the contemplative pose. Lives in the
                    top-right of the hero on desktop; hides at narrow
                    widths so the mobile layout stays compact. */}
                <div className="bull-float dashboard-mood" style={{ display: 'inline-flex' }}>
                    {(kpis.totalReturnPct || 0) > 0
                        ? <FX.BullCelebrating size={160} />
                        : <FX.BullContemplative size={160} />}
                </div>
            </div>

            {/* Editorial pull-quote — frames the section change from
                "what you ran" to "what came back". Drop-cap serif on
                a hairline left rail; takes ~50px of vertical space. */}
            <FX.PullQuote attribution={`${strategy.name} · ${UI.fmtDate(new Date(endDate))}`}>
                {(kpis.totalReturnPct || 0) > 0
                    ? <>The capital was committed. The signals fired. The story below is what the strategy made of it.</>
                    : <>Not every backtest tells a winning story. The trades, the drawdown, the lesson — it's all in the numbers below.</>}
            </FX.PullQuote>

            <div className="fade-in grid-kpi-5 mb-5">
                <UI.KPITile label="Total return"
                    value={UI.fmtPct(kpis.totalReturnPct || 0)}
                    delta={`${(kpis.totalReturnPct || 0) >= 0 ? 'Profit' : 'Loss'}`}
                    good={(kpis.totalReturnPct || 0) >= 0 ? 'up' : 'down'}
                    highlight={(kpis.totalReturnPct || 0) > 0}
                    hint={<>
                        <strong>Total Return</strong> — final portfolio value vs the
                        capital you committed, as a percent. Includes both <em>realised</em>
                        gains (closed sells) and <em>unrealised</em> gains (open holdings
                        marked-to-market on the last day). Formula: <code>(final / initial − 1) × 100</code>.
                    </>} />
                <UI.KPITile label="CAGR"
                    value={UI.fmtPct(kpis.cagrPct || 0)}
                    good={(kpis.cagrPct || 0) >= 0 ? 'up' : 'down'}
                    deltaLabel="annualised"
                    delta={(kpis.cagrPct || 0) > 14 ? 'Strong' : 'Moderate'}
                    stellar={(kpis.cagrPct || 0) > 20}
                    hint={<>
                        <strong>Compound Annual Growth Rate</strong> — the constant annual
                        return that would have produced the same final value over the same
                        calendar window. Formula: <code>(final / initial)^(1/years) − 1</code>.
                        Backtest windows shorter than a year extrapolate the partial-period
                        return out to a full year, so a strong 3-month run can show a
                        triple-digit CAGR. SIP runs over-state CAGR because money wasn't
                        invested for the full window.
                    </>} />
                <UI.KPITile label="Max drawdown"
                    value={UI.fmtPct(kpis.maxDrawdownPct || 0)}
                    good="down"
                    delta="Peak to trough"
                    hint={<>
                        <strong>Maximum Drawdown</strong> — largest peak-to-trough drop of
                        the total portfolio value during the run, as a negative percent.
                        Formula: walk the curve, track the running peak, return the lowest
                        observed <code>(value − peak) / peak</code>. A risk-of-ruin proxy:
                        deeper drawdowns mean longer recovery time and more emotional pain
                        in real money.
                    </>} />
                <UI.KPITile label="Sharpe ratio"
                    value={(kpis.sharpeRatio || 0).toFixed(2)}
                    good={(kpis.sharpeRatio || 0) >= 1 ? 'up' : 'down'}
                    delta="Risk-adjusted"
                    hint={<>
                        <strong>Sharpe Ratio</strong> — risk-adjusted return: average daily
                        portfolio return divided by the standard deviation of those returns,
                        annualised by <code>√252</code>. Risk-free rate is treated as zero.
                        Rules of thumb: <strong>&gt;1</strong> decent,
                        <strong> &gt;2</strong> strong, <strong>&gt;3</strong> exceptional.
                        Caveat: SIP mode tends to over-state Sharpe because monthly cash
                        injections show up as positive return days.
                    </>} />
                <UI.KPITile label="Win rate"
                    value={`${(kpis.winRatePct || 0).toFixed(0)}%`}
                    delta={`${kpis.winningTrades || 0}/${kpis.closedTrades || 0} trades`}
                    good={(kpis.winRatePct || 0) >= 50 ? 'up' : 'down'}
                    hint={<>
                        <strong>Win Rate</strong> — closing trades (sells) with positive
                        realised P&amp;L divided by total closing trades. Buys don't count —
                        they're only opening trades. Ignores currently-held positions, even
                        if they're up or down on paper. A 50%+ win rate is good; even
                        sub-50% can be profitable if winners are larger than losers.
                    </>} />
            </div>

            <div className="fade-in mb-5">
                <PortfolioChart
                    curve={curve}
                    benchmarkCurve={summary.benchmarkCurve}
                    benchmarkLabel={summary.benchmarkLabel} />
            </div>

            <div className="fade-in">
                <TradesTable trades={trades} windowStart={startDate} windowEnd={endDate} />
            </div>

            <div className="fade-in">
                <HoldingsCard summary={summary} />
            </div>
        </div>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
